import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { VITE_API_URL, VITE_BRIDGE_URL } from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 延迟执行函数
 * @param ms 延迟时间（毫秒）
 * @returns Promise<void>
 * @example
 * // 延迟 1 秒
 * await sleep(1000);
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const userPalmIDFromId = (userId?: string) => {
  if (!userId) {
    return "";
  }
  return `0x${userId.split("-").join("")}`;
};

export const shortId = (address: string) => {
  if (typeof address !== "string" || address.length < 10) {
    return address;
  }
  return (
    address.substring(0, 4) + "..." + address.substring(address.length - 6)
  );
};

const keyTimeMap = {} as Record<string, number>;
export const isLessTime = (key: string, time = 1000): boolean => {
  const now = Date.now();
  let returnValue = true;
  if (keyTimeMap[key]) {
    returnValue = now - keyTimeMap[key] > time;
  }
  if (returnValue) {
    keyTimeMap[key] = now;
  }
  return returnValue;
};

export const isDemoVersion = () => {
  return location.host.includes("demo");
};

export const bridgeUrl = () => {
  return isDemoVersion()
    ? "https://bridge.demo.very.org/api/v1/session"
    : VITE_BRIDGE_URL;
};

export const apiUrl = () => {
  return isDemoVersion() ? "https://api.demo.very.org/v1" : VITE_API_URL;
};

/**
 * Get mobile status bar height using multiple methods for better compatibility
 * @returns Status bar height in pixels
 */
export function getStatusBarHeight(): number {
  // Method 1: Visual Viewport API (most reliable)
  if (window.visualViewport) {
    const statusBarHeight = window.innerHeight - window.visualViewport.height;
    if (statusBarHeight > 0) {
      return statusBarHeight;
    }
  }

  // Method 2: CSS Environment Variables (iOS Safari)
  if (CSS.supports("height", "env(safe-area-inset-top)")) {
    const style = getComputedStyle(document.documentElement);
    const safeAreaTop = style.getPropertyValue("env(safe-area-inset-top)");
    if (safeAreaTop) {
      const height = parseInt(safeAreaTop, 10);
      if (height > 0) {
        return height;
      }
    }
  }

  // Method 3: Detect iOS and use common status bar heights
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    // Common iOS status bar heights
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const statusBarHeight = screenHeight - windowHeight;

    // iOS status bar is typically 20px (portrait) or 44px (landscape)
    if (statusBarHeight > 0 && statusBarHeight <= 44) {
      return statusBarHeight;
    }
  }

  // Method 4: Android detection
  const isAndroid = /Android/.test(navigator.userAgent);
  if (isAndroid) {
    // Android status bar is typically 24px or 25px
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const statusBarHeight = screenHeight - windowHeight;

    if (statusBarHeight > 0 && statusBarHeight <= 30) {
      return statusBarHeight;
    }
  }

  return 0;
}

/**
 * Apply status bar height to document element as CSS custom property
 */
export function applyStatusBarHeight(): void {
  const statusBarHeight = getStatusBarHeight();
  document.documentElement.style.setProperty(
    "--status-bar-height",
    `${statusBarHeight}px`
  );
}

/**
 * Fix viewport height for iOS WebView to prevent scrolling
 * This function sets fixed height on html, body, and #root elements
 */
export function fixIOSWebViewHeight(): void {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  const setHeight = () => {
    const vh = window.innerHeight;
    const root = document.getElementById("root");
    const html = document.documentElement;
    const body = document.body;

    if (root) {
      root.style.height = `${vh}px`;
    }
    if (html) {
      html.style.height = `${vh}px`;
    }
    if (body) {
      body.style.height = `${vh}px`;
    }
  };

  // Set height immediately
  setHeight();

  // Update height on resize/orientation change
  window.addEventListener("resize", setHeight);
  window.addEventListener("orientationchange", () => {
    setTimeout(setHeight, 100);
  });

  // Prevent scrolling
  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";
}

const day = 1000 * 60 * 60 * 24;

export const deletedDays = (scheduledDeletionAt?: string | null) => {
  if (!scheduledDeletionAt) return "90 days";
  const now = new Date();
  const scheduledDeletionDate = new Date(scheduledDeletionAt);
  const diffTime = Math.abs(
    scheduledDeletionDate.getTime() + 90 * day - now.getTime()
  );
  const diffDays = Math.ceil(diffTime / day);

  // Ensure diffDays is between 0 and 90
  const boundedDiffDays = Math.max(0, Math.min(diffDays, 90));

  return `${boundedDiffDays} day${boundedDiffDays > 1 ? "s" : ""}`;
};

export const videoAspectRatio = () => {
  if (window.innerHeight < window.innerWidth || window.innerHeight < 700) {
    return 3 / 4;
  }
  return 3 / 4;
};

/**
 * Capture image from video element
 * @param videoElement - HTMLVideoElement to capture from
 * @param format - Output format: 'base64', 'blob', 'blobUrl', 'imageData', or 'file'
 * @param quality - Image quality (0-1) for JPEG format, default 0.92
 * @param mimeType - MIME type for image, default 'image/jpeg'
 * @returns Promise with captured image in specified format
 * @example
 * // Get base64 string
 * const base64 = await captureImageFromVideo(videoElement, 'base64');
 *
 * // Get blob URL
 * const blobUrl = await captureImageFromVideo(videoElement, 'blobUrl');
 *
 * // Get ImageData for canvas processing
 * const imageData = await captureImageFromVideo(videoElement, 'imageData');
 */
export async function captureImageFromVideo(
  videoElement: HTMLVideoElement,
  format: "base64" | "blob" | "blobUrl" | "imageData" | "file" = "base64",
  quality: number = 0.92,
  mimeType: string = "image/jpeg"
): Promise<string | Blob | ImageData | File | null> {
  if (!videoElement) {
    console.error("Video element is required");
    return null;
  }

  const width = videoElement.videoWidth;
  const height = videoElement.videoHeight;

  if (width === 0 || height === 0) {
    console.error("Video dimensions are invalid");
    return null;
  }

  // Create a temporary canvas
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    console.error("Failed to get canvas context");
    return null;
  }

  // Draw video frame to canvas
  context.drawImage(videoElement, 0, 0, width, height);

  // Return based on requested format
  switch (format) {
    case "imageData": {
      // Return ImageData object for canvas processing
      return context.getImageData(0, 0, width, height);
    }

    case "base64": {
      // Return base64 data URL string
      return canvas.toDataURL(mimeType, quality);
    }

    case "blob": {
      // Return Blob object
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to create blob"));
            }
          },
          mimeType,
          quality
        );
      });
    }

    case "blobUrl": {
      // Return blob URL string
      return new Promise<string>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              resolve(url);
            } else {
              reject(new Error("Failed to create blob"));
            }
          },
          mimeType,
          quality
        );
      });
    }

    case "file": {
      // Return File object
      return new Promise<File>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File(
                [blob],
                `capture-${Date.now()}.${mimeType.split("/")[1]}`,
                {
                  type: mimeType,
                }
              );
              resolve(file);
            } else {
              reject(new Error("Failed to create file"));
            }
          },
          mimeType,
          quality
        );
      });
    }

    default:
      console.error(`Unsupported format: ${format}`);
      return null;
  }
}

export function isSupportWasmSIMD() {
  const ua = navigator.userAgent;

  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);

  let iOSVersion = null;
  if (isIOS) {
    const match = ua.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match) {
      iOSVersion = {
        major: parseInt(match[1], 10),
        minor: parseInt(match[2], 10),
        patch: parseInt(match[3] ?? "0", 10),
      };
      const isIOSGte164 =
        iOSVersion.major > 16 ||
        (iOSVersion.major === 16 && iOSVersion.minor >= 4);
      return isIOSGte164;
    }
  }

  if (isAndroid) {
    let chromeVersion = null;
    const chromeMatch = ua.match(/Chrome\/(\d+)\./);
    if (chromeMatch) {
      chromeVersion = parseInt(chromeMatch[1]);
    }
    if (chromeVersion && chromeVersion > 0) {
      return chromeVersion >= 91;
    }
  }
  return true;
}

interface Navigator {
  deviceMemory?: number;
}

export const isUseGesture = () => {
  const isMobile = /Mobile|Android|iPad|iPod|Mobile|Tablet/i.test(
    navigator.userAgent
  );
  const ramGB = (navigator as Navigator).deviceMemory ?? 8;
  return isMobile && ramGB <= 6;
};

